import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Label, Modal, Select, Spinner, TextInput } from "flowbite-react";
import { HiOutlineTrash } from "react-icons/hi2";
import { HiLockClosed } from "react-icons/hi";
import { FaWhatsapp, FaInstagram, FaTiktok, FaFacebook, FaPhone } from "react-icons/fa";
import { FaXTwitter, FaMapLocationDot } from "react-icons/fa6";
import { SiGooglemaps } from "react-icons/si";
import { useSelector } from "react-redux";
import {
  atualizarLojaLink,
  criarLojaLink,
  deletarLojaLink,
  getLojaLinks,
} from "../../api/user";
import { buildLinkHref, getLinkTypeLabel } from "../../utils/storeLinks";

const LINK_TYPE_OPTIONS = [
  "whatsapp",
  "instagram",
  "facebook",
  "tiktok",
  "twitter_x",
  "geolocalizacao",
  "endereco",
  "telefone",
];

const emptyForm = {
  tipo: "whatsapp",
  label: "",
  valor: "",
};

const LINK_TYPE_ICON = {
  whatsapp: FaWhatsapp,
  instagram: FaInstagram,
  facebook: FaFacebook,
  tiktok: FaTiktok,
  twitter_x: FaXTwitter,
  geolocalizacao: SiGooglemaps,
  endereco: FaMapLocationDot,
  telefone: FaPhone,
};

const NON_CLICKABLE_TYPES = new Set(["telefone", "endereco"]);

function normalizeForm(form) {
  const rawValue = form.valor?.trim() || "";
  const payload = {
    tipo: form.tipo,
    label: form.label?.trim() || null,
    valor: rawValue,
  };

  // WhatsApp aceita numero (normalizado) ou link direto (ex.: grupo).
  if (payload.tipo === "whatsapp") {
    const isUrl = /^https?:\/\//i.test(rawValue);
    payload.valor = isUrl ? rawValue : rawValue.replace(/\D/g, "");
  }

  if (payload.tipo === "telefone") {
    payload.valor = payload.valor.replace(/\D/g, "");
  }

  return payload;
}

const LojaLinksManager = ({ canEdit = true, onChanged = null, notify = () => {} }) => {
  const profile = useSelector((state) => state.user.profile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLinks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLojaLinks();
      setLinks(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        message: error.message || "Erro ao carregar links.",
        color: "failure",
      });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const groupedLinks = useMemo(() => {
    const groups = {};
    for (const link of links) {
      if (!groups[link.tipo]) groups[link.tipo] = [];
      groups[link.tipo].push(link);
    }
    return groups;
  }, [links]);
  const limiteLinks = profile?.permissoesPlano?.limiteLinks;
  const possuiLimiteLinks =
    Number.isFinite(Number(limiteLinks)) && Number(limiteLinks) >= 0;
  const atingiuLimiteLinks = possuiLimiteLinks && links.length >= Number(limiteLinks);

  const openCreateModal = () => {
    if (atingiuLimiteLinks) {
      notify({
        message: `Seu plano permite ate ${limiteLinks} link(s) ativo(s).`,
        color: "warning",
      });
      return;
    }

    setEditingLink(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEditModal = (link) => {
    setEditingLink(link);
    setForm({
      tipo: link.tipo || "whatsapp",
      label: link.label || "",
      valor: link.valor || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingLink(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!canEdit) return;

    const payload = normalizeForm(form);
    if (!payload.tipo || !payload.valor) {
      notify({
        message: "Preencha tipo e valor do link.",
        color: "warning",
      });
      return;
    }

    try {
      setSaving(true);
      if (editingLink?.id) {
        await atualizarLojaLink(editingLink.id, payload);
      } else {
        await criarLojaLink(payload);
      }

      await loadLinks();
      if (onChanged) onChanged();
      notify({
        message: editingLink ? "Link atualizado com sucesso." : "Link criado com sucesso.",
        color: "success",
      });
      closeModal();
    } catch (error) {
      notify({
        message: error.message || "Erro ao salvar link.",
        color: "failure",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (linkId) => {
    if (!canEdit) return;
    try {
      await deletarLojaLink(linkId);
      await loadLinks();
      if (onChanged) onChanged();
      notify({
        message: "Link removido com sucesso.",
        color: "success",
      });
    } catch (error) {
      notify({
        message: error.message || "Erro ao remover link.",
        color: "failure",
      });
    }
  };

  const getIconByType = (tipo) => LINK_TYPE_ICON[tipo] || FaMapLocationDot;
  const selectedTypeIcon = getIconByType(form.tipo);

  return (
    <section className="mt-10 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-800">Links e Contatos</h2>
        <Button
          size="sm"
          gradientDuoTone="purpleToPink"
          onClick={openCreateModal}
          disabled={!canEdit || atingiuLimiteLinks}
        >
          Adicionar Link
        </Button>
      </div>

      {possuiLimiteLinks ? (
        <div
          className={`rounded-lg border px-3 py-2 text-sm ${
            atingiuLimiteLinks
              ? "border-amber-200 bg-amber-50 text-amber-900"
              : "border-gray-200 bg-gray-50 text-gray-700"
          }`}
        >
          <div className="flex items-center gap-2">
            {atingiuLimiteLinks ? <HiLockClosed className="shrink-0" /> : null}
            <span>
              {links.length} de {limiteLinks} link(s) ativos usados.
            </span>
          </div>
          {atingiuLimiteLinks ? (
            <p className="mt-1">
              Voce atingiu o limite do seu plano. Remova um link ou faca upgrade
              para adicionar novos canais.
            </p>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Spinner size="sm" /> Carregando links...
        </div>
      ) : null}

      {!loading && links.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-600">
            Nenhum link cadastrado ainda. Adicione WhatsApp, Instagram, endereco e outros
            canais para aparecer no catalogo.
          </p>
        </Card>
      ) : null}

      {!loading &&
        Object.entries(groupedLinks).map(([tipo, tipoLinks]) => (
          <div key={tipo} className="space-y-3">
            <div className="flex items-center gap-2">
              {(() => {
                const Icon = getIconByType(tipo);
                return <Icon className="text-gray-600" />;
              })()}
              <h3 className="text-sm font-semibold text-gray-700">{getLinkTypeLabel(tipo)}</h3>
              <Badge color="info">{tipoLinks.length}</Badge>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {tipoLinks.map((link) => (
                <Card key={link.id}>
                  <div className="space-y-2">
                    {link.label ? (
                      <div className="flex items-center gap-2">
                        {(() => {
                          const Icon = getIconByType(link.tipo);
                          return <Icon className="text-gray-600" />;
                        })()}
                        <p className="font-medium text-gray-900">{link.label}</p>
                      </div>
                    ) : null}

                    {(() => {
                      const Icon = getIconByType(link.tipo);
                      const href = buildLinkHref(link);
                      const canClick = Boolean(href) && !NON_CLICKABLE_TYPES.has(link.tipo);

                      if (canClick) {
                        return (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm break-all text-gray-700 hover:text-purple-700 hover:underline"
                          >
                            {!link.label ? (
                              <Icon className="shrink-0 text-gray-600" />
                            ) : null}
                            <span>{link.valor}</span>
                          </a>
                        );
                      }

                      return (
                        <p className="inline-flex items-center gap-2 text-sm text-gray-700 break-all">
                          {!link.label ? (
                            <Icon className="shrink-0 text-gray-600" />
                          ) : null}
                          <span>{link.valor}</span>
                        </p>
                      );
                    })()}

                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="xs"
                        color="light"
                        onClick={() => openEditModal(link)}
                        disabled={!canEdit}
                      >
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        color="light"
                        className="text-rose-600 hover:bg-rose-50"
                        onClick={() => handleDelete(link.id)}
                        disabled={!canEdit}
                      >
                        <HiOutlineTrash className="mr-1.5" />
                        Excluir
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}

      <Modal show={modalOpen} onClose={closeModal} size="md">
        <Modal.Header>{editingLink ? "Editar Link" : "Adicionar Link"}</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="tipo" className="mb-2 block">
                Tipo
              </Label>
              <div className="mb-2 inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1 text-sm text-gray-700">
                {(() => {
                  const Icon = selectedTypeIcon;
                  return <Icon />;
                })()}
                <span>{getLinkTypeLabel(form.tipo)}</span>
              </div>
              <Select
                id="tipo"
                value={form.tipo}
                onChange={(e) => setForm((prev) => ({ ...prev, tipo: e.target.value }))}
              >
                {LINK_TYPE_OPTIONS.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {getLinkTypeLabel(tipo)}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <Label htmlFor="label" className="mb-2 block">
                Rotulo (opcional)
              </Label>
              <TextInput
                id="label"
                placeholder="Ex.: Consultora Ana, Outlet, Matriz..."
                value={form.label}
                onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="valor" className="mb-2 block">
                Valor{" "}
                {form.tipo === "telefone"
                  ? "(somente numeros)"
                  : form.tipo === "whatsapp"
                  ? "(numero ou link de grupo)"
                  : ""}
              </Label>
              <TextInput
                id="valor"
                placeholder={
                  form.tipo === "whatsapp"
                    ? "XX XXXXX XXXX ou https://chat.whatsapp.com/..."
                    : form.tipo === "telefone"
                    ? "XX XXXX XXXX"
                    : form.tipo === "endereco"
                    ? "Endereco completo ou link do mapa"
                    : "https://..."
                }
                value={form.valor}
                onChange={(e) => setForm((prev) => ({ ...prev, valor: e.target.value }))}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button gradientDuoTone="purpleToPink" onClick={handleSave} isProcessing={saving}>
            {editingLink ? "Salvar Alteracoes" : "Adicionar Link"}
          </Button>
          <Button color="gray" onClick={closeModal} disabled={saving}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default LojaLinksManager;
